/**
 * Hook for managing ResponsiveCommandSelect state
 * Consolidates all state management concerns into a single hook
 */

import React from "react";
import type { DropdownPosition, ResponsiveSelectState, ResponsiveSelectHandlers } from "@/lib/types/select-components";

export function useResponsiveSelectState(onOpenChangeProp?: (open: boolean) => void) {
  const [open, setOpenState] = React.useState(false);
  const [internalSearch, setInternalSearch] = React.useState("");
  const [triggerWidth, setTriggerWidth] = React.useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = React.useState<DropdownPosition | null>(null);
  const [recentsNonce, setRecentsNonce] = React.useState(0);
  const [tooltipContainer, setTooltipContainer] = React.useState<HTMLElement | null>(null);

  const setOpen = React.useCallback(
    (arg: boolean | ((prev: boolean) => boolean)) => {
      setOpenState((prev) => {
        const next = typeof arg === "function" ? arg(prev) : arg;
        onOpenChangeProp?.(next);
        return next;
      });
    },
    [onOpenChangeProp]
  );

  const state: ResponsiveSelectState = React.useMemo(
    () => ({
      open,
      internalSearch,
      triggerWidth,
      dropdownPosition,
      recentsNonce,
      tooltipContainer,
    }),
    [open, internalSearch, triggerWidth, dropdownPosition, recentsNonce, tooltipContainer]
  );

  const handlers: ResponsiveSelectHandlers = React.useMemo(
    () => ({
      setOpen,
      setInternalSearch,
      setTriggerWidth,
      setDropdownPosition,
      setRecentsNonce,
      setTooltipContainer,
      handleSelect: () => {}, // Will be set by parent
      handleClearRecents: () => {}, // Will be set by parent
    }),
    [setOpen]
  );

  return { state, handlers, setOpen, setInternalSearch, setTriggerWidth, setDropdownPosition, setRecentsNonce, setTooltipContainer };
}
